import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Home, ChevronRight, Plus, X, Cog, Loader2, Edit3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';

const STANDARD_HEAT_TREATMENTS = [
  'None', 'Grade 4.6', 'Grade 8.8', 'Grade 10.9', 'Grade 11.9', 'Grade 12.9',
  'Grade 8', 'Grade 10', 'Condition T', 'Condition V', 'Condition U',
  'Normalise', 'Anneal', 'Sub-Critical Anneal', 'Case Harden'
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CreatePart = () => {
  const navigate = useNavigate();
  const [processes, setProcesses] = useState([]);
  const [formData, setFormData] = useState({
    part_number: '',
    drawing_number: '',
    description: '',
    diameter: '',
    loc: '',
    material_grade: '',
    heat_treatment: 'None',
    process_sequence: [],
    sop_links: {}
  });
  const [selectedProcess, setSelectedProcess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [customHeatTreatmentDialog, setCustomHeatTreatmentDialog] = useState(false);
  const [customHeatTreatmentInput, setCustomHeatTreatmentInput] = useState('');

  useEffect(() => {
    fetchProcesses();
  }, []);

  const fetchProcesses = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/processes`);
      setProcesses(response.data.processes);
    } catch (error) {
      console.error('Error fetching processes:', error);
    }
  };

  const handleInputChange = (field, value) => {
    if (field === 'heat_treatment' && value === 'Custom') {
      setCustomHeatTreatmentDialog(true);
      return;
    }
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCustomHeatTreatmentSave = () => {
    if (!customHeatTreatmentInput.trim()) {
      toast.error('Please enter a custom heat treatment');
      return;
    }
    setFormData(prev => ({ ...prev, heat_treatment: customHeatTreatmentInput.trim() }));
    setCustomHeatTreatmentDialog(false);
    setCustomHeatTreatmentInput('');
    toast.success('Custom heat treatment added');
  };

  const addProcess = () => {
    if (selectedProcess) {
      setFormData(prev => ({
        ...prev,
        process_sequence: [...prev.process_sequence, selectedProcess]
      }));
      setSelectedProcess('');
    }
  };

  const removeProcess = (index) => {
    setFormData(prev => ({
      ...prev,
      process_sequence: prev.process_sequence.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.part_number || !formData.drawing_number || !formData.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.process_sequence.length === 0) {
      toast.error('Please add at least one process step');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/parts`, {
        ...formData,
        diameter: parseFloat(formData.diameter) || 0,
        loc: parseFloat(formData.loc) || 0
      });
      toast.success('Part created successfully');
      navigate('/engineering');
    } catch (error) {
      console.error('Error creating part:', error);
      toast.error(error.response?.data?.detail || 'Failed to create part');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] relative overflow-hidden text-white">
      {/* Honeycomb Background */}
      <div className="fixed inset-0 opacity-30 pointer-events-none">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="honeycomb-cp" x="0" y="0" width="56" height="100" patternUnits="userSpaceOnUse">
              <path d="M28 0L56 16.5V49.5L28 66L0 49.5V16.5L28 0Z" fill="none" stroke="rgba(59, 130, 246, 0.12)" strokeWidth="1"/>
              <path d="M28 66L56 82.5V115.5L28 132L0 115.5V82.5L28 66Z" fill="none" stroke="rgba(59, 130, 246, 0.12)" strokeWidth="1"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#honeycomb-cp)" />
        </svg>
      </div>
      <div className="fixed inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at center, rgba(59, 130, 246, 0.06) 0%, transparent 70%)' }} />

      {/* Fixed Header */}
      <header className="fixed top-0 left-0 right-0 z-50 h-16 bg-slate-950/90 backdrop-blur-xl border-b border-blue-500/20">
        <div className="h-full max-w-[1920px] mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/engineering')} className="text-slate-400 hover:text-blue-400 hover:bg-blue-500/10" data-testid="back-btn">
              <Home className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 border border-blue-500/30 flex items-center justify-center">
                <Cog className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white uppercase tracking-wide" data-testid="page-title">Create Part</h1>
                <p className="text-xs text-slate-500">Engineering Database</p>
              </div>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-2 text-sm">
            <span className="text-slate-500">Engineering</span>
            <ChevronRight className="w-4 h-4 text-slate-600" />
            <span className="text-blue-400 font-medium">New Part</span>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 pt-20 px-4 sm:px-6 pb-8">
        <div className="max-w-[900px] mx-auto">
          <form onSubmit={handleSubmit} className="bg-slate-900/40 backdrop-blur-md border border-blue-500/10 rounded-xl p-8" data-testid="create-part-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Part Number *</Label>
                <Input
                  data-testid="part-number-input"
                  value={formData.part_number}
                  onChange={(e) => handleInputChange('part_number', e.target.value)}
                  className="bg-slate-950/50 border-slate-800 font-mono"
                  placeholder="P-12345"
                  required
                />
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Drawing Number *</Label>
                <Input
                  data-testid="drawing-number-input"
                  value={formData.drawing_number}
                  onChange={(e) => handleInputChange('drawing_number', e.target.value)}
                  className="bg-slate-950/50 border-slate-800 font-mono"
                  placeholder="DWG-12345"
                  required
                />
              </div>

              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Description *</Label>
                <Textarea
                  data-testid="description-input"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  className="bg-slate-950/50 border-slate-800"
                  placeholder="Part description..."
                  rows={2}
                  required
                />
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Material Grade</Label>
                <Input
                  data-testid="material-grade-input"
                  value={formData.material_grade}
                  onChange={(e) => handleInputChange('material_grade', e.target.value)}
                  className="bg-slate-950/50 border-slate-800"
                  placeholder="EN24, 4140, etc."
                />
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Diameter (mm)</Label>
                <Input
                  type="number"
                  step="0.01"
                  data-testid="diameter-input"
                  value={formData.diameter}
                  onChange={(e) => handleInputChange('diameter', e.target.value)}
                  className="bg-slate-950/50 border-slate-800"
                  placeholder="25.4"
                />
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Length of Cut (mm)</Label>
                <Input
                  type="number"
                  step="0.01"
                  data-testid="loc-input"
                  value={formData.loc}
                  onChange={(e) => handleInputChange('loc', e.target.value)}
                  className="bg-slate-950/50 border-slate-800"
                  placeholder="150"
                />
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Heat Treatment Specification</Label>
                <Select value={STANDARD_HEAT_TREATMENTS.includes(formData.heat_treatment) ? formData.heat_treatment : 'custom-value'} onValueChange={(value) => handleInputChange('heat_treatment', value)}>
                  <SelectTrigger data-testid="heat-treatment-select" className="bg-slate-950/50 border-slate-800">
                    <SelectValue>
                      {formData.heat_treatment}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {STANDARD_HEAT_TREATMENTS.map((ht) => (
                      <SelectItem key={ht} value={ht}>{ht}</SelectItem>
                    ))}
                    <SelectItem value="Custom" className="text-cyan-400 font-medium border-t border-slate-700 mt-1 pt-1">
                      <span className="flex items-center gap-2">
                        <Edit3 className="w-3 h-3" />
                        Custom...
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
                {!STANDARD_HEAT_TREATMENTS.includes(formData.heat_treatment) && formData.heat_treatment !== 'None' && (
                  <p className="text-xs text-cyan-400 mt-1">Custom: {formData.heat_treatment}</p>
                )}
              </div>
            </div>

            {/* Process Sequence */}
            <div className="mt-8 pt-8 border-t border-slate-800">
              <Label className="text-xs uppercase tracking-wider text-slate-500 mb-4 block">Process Sequence *</Label>
              
              <div className="flex gap-2 mb-4">
                <Select value={selectedProcess} onValueChange={setSelectedProcess}>
                  <SelectTrigger data-testid="process-select" className="bg-slate-950/50 border-slate-800 flex-1">
                    <SelectValue placeholder="Select process" />
                  </SelectTrigger>
                  <SelectContent>
                    {processes.map((proc) => (
                      <SelectItem key={proc} value={proc}>{proc}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  onClick={addProcess}
                  data-testid="add-process-btn"
                  className="bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 border border-blue-500/50"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>

              <div className="flex flex-wrap gap-2" data-testid="process-sequence-list">
                {formData.process_sequence.map((proc, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-2 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg"
                    data-testid="process-item"
                  >
                    <span className="font-mono text-white text-sm">{idx + 1}. {proc}</span>
                    <button
                      type="button"
                      onClick={() => removeProcess(idx)}
                      data-testid="remove-process-btn"
                      className="text-red-400 hover:text-red-300"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Submit */}
            <div className="mt-8 flex gap-4 pt-6 border-t border-slate-800">
              <Button
                type="submit"
                disabled={submitting}
                data-testid="submit-btn"
                className="bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 border border-blue-500/50 hover:shadow-[0_0_20px_rgba(59,130,246,0.3)] font-bold uppercase tracking-wide"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                {submitting ? 'Creating...' : 'Create Part'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/engineering')}
                data-testid="cancel-btn"
                className="border-slate-700 hover:border-slate-600"
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </main>

      {/* Custom Heat Treatment Dialog */}
      <Dialog open={customHeatTreatmentDialog} onOpenChange={setCustomHeatTreatmentDialog}>
        <DialogContent className="bg-slate-900 border-blue-500/30 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white uppercase tracking-wide flex items-center gap-2">
              <Edit3 className="w-5 h-5 text-blue-400" />
              Custom Heat Treatment
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Enter a custom heat treatment specification
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label className="text-slate-400 uppercase text-xs">Heat Treatment Specification</Label>
              <Input
                value={customHeatTreatmentInput}
                onChange={(e) => setCustomHeatTreatmentInput(e.target.value)}
                placeholder="e.g., Quench & Temper to 28-32 HRC"
                className="bg-slate-800 border-slate-700 mt-2"
                data-testid="custom-heat-treatment-input"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleCustomHeatTreatmentSave();
                  }
                }}
              />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setCustomHeatTreatmentDialog(false);
                  setCustomHeatTreatmentInput('');
                }}
                className="border-slate-600"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCustomHeatTreatmentSave}
                className="bg-blue-600 hover:bg-blue-500"
                data-testid="save-custom-heat-treatment-btn"
              >
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CreatePart;
